import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "We've hired three agencies. Z Crazy is the only one that made our board sit up straight. 14 days, no hand-holding, the site now outranks every competitor on brand searches.",
    name: "Mira Koenig",
    role: "Co-founder, Lumen & Sons",
    location: "Zürich",
    accent: "A",
  },
  {
    quote:
      "They refused to use a page builder. At first I was annoyed. Then I saw the Lighthouse score and the first week's analytics. I owe them an apology.",
    name: "Devon Ashe",
    role: "VP Marketing, ATLAS/CO",
    location: "Brooklyn, NY",
    accent: "B",
  },
  {
    quote:
      "The site feels like a film still you can click through. Our conversion on the pre-order page jumped 4.6× in the first month. I keep sending the URL to friends.",
    name: "Priya Narang",
    role: "Founder, Vellum",
    location: "Lisbon",
    accent: "C",
  },
  {
    quote:
      "Zero bloat. Semantic HTML our next engineer can actually read. And the art direction is on par with the work we see out of Linear or Rauno. Worth every cent.",
    name: "Oskar Brandt",
    role: "Design Director, Northgate",
    location: "Copenhagen",
    accent: "D",
  },
];

const TestimonialsSection = () => {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % TESTIMONIALS.length), 7000);
    return () => clearInterval(t);
  }, []);

  const go = (d) => setI((v) => (v + d + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[i];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-container"
      className="relative py-32 sm:py-44 overflow-hidden"
    >
      <div className="max-w-[1280px] mx-auto px-6 sm:px-10">
        <div className="flex items-end justify-between mb-16 gap-6">
          <div>
            <div className="mono-label mb-4">— 05 / Witnesses</div>
            <h2 className="font-display font-light tracking-tight text-[clamp(2.2rem,4.6vw,4.4rem)] leading-[0.95] max-w-[18ch]">
              Not blurbs.
              <br />
              <span className="font-editorial italic text-accent">Testimonies.</span>
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => go(-1)}
              aria-label="Previous"
              className="h-12 w-12 rounded-full border border-white/10 hover:border-accent/40 hover:bg-accent/5 flex items-center justify-center transition-all"
              data-testid="testimonial-prev"
            >
              <ArrowLeft size={16} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next"
              className="h-12 w-12 rounded-full border border-white/10 hover:border-accent/40 hover:bg-accent/5 flex items-center justify-center transition-all"
              data-testid="testimonial-next"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <div className="relative min-h-[360px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
              data-testid="testimonial-quote-text"
            >
              <div className="absolute -top-16 left-0 font-editorial italic text-[10rem] text-accent/10 leading-none select-none">
                “
              </div>

              <p className="relative font-display font-light text-[clamp(1.6rem,3.2vw,3rem)] tracking-tight leading-[1.15] text-[#F4F4F2] max-w-[22ch]">
                {t.quote}
              </p>

              <div className="mt-12 flex items-center gap-5">
                <div className="h-14 w-14 rounded-full glass flex items-center justify-center font-display font-semibold text-accent text-lg">
                  {t.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div>
                  <div className="font-display font-semibold text-[#F4F4F2]">
                    {t.name}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#5A5D68]">
                    {t.role} — {t.location}
                  </div>
                </div>
              </div>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center gap-3">
          {TESTIMONIALS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              aria-label={`Testimonial ${idx + 1}`}
              className="group"
            >
              <motion.div
                animate={{ width: idx === i ? 64 : 16 }}
                className={`h-[2px] transition-colors ${
                  idx === i ? "bg-accent" : "bg-white/15 group-hover:bg-white/40"
                }`}
              />
            </button>
          ))}
          <div className="ml-4 font-mono text-[11px] text-[#5A5D68]">
            {String(i + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
